import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getProduct, addToCart, getReviews } from '../api/api';
import { ShoppingCart, Star, Truck, Shield, ArrowLeft } from 'lucide-react';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import ReviewForm from '../components/ReviewForm';

const ProductDetail = ({ sessionId }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [selectedWeight, setSelectedWeight] = useState('250G');
  const [selectedColor, setSelectedColor] = useState('Siyah');
  const [selectedGrind, setSelectedGrind] = useState('Çekirdek (Öğütme İstemiyorum)');
  const [selectedPrice, setSelectedPrice] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isEspressoMachine, setIsEspressoMachine] = useState(false);

  const grindOptions = [
    'Çekirdek (Öğütme İstemiyorum)',
    'Chemex',
    'V60',
    'Espresso',
    'Moka Pot',
    'Filtre Kahve Makinesi',
    'Aeropress',
    'French Press',
    'Cold Brew'
  ];

  useEffect(() => {
    loadProduct();
    loadReviews();
  }, [id]);

  const getProductImage = () => {
    if (isEspressoMachine && product.name.includes('Fiamma Compass')) {
      if (selectedColor === 'Beyaz') {
        return '/images/fiamma-compass-white.jpg';
      } else {
        return '/images/fiamma-compass-black.jpg';
      }
    }
    return product.image;
  };

  useEffect(() => {
    if (product && product.variants && product.variants.length > 0) {
      // Check if product is espresso machine or coffee
      const isEspresso = product.category === 'Espresso Makineleri';
      setIsEspressoMachine(isEspresso);
      
      if (isEspresso) {
        const variant = product.variants.find(v => v.color === selectedColor);
        if (variant) {
          setSelectedPrice(variant.price);
        }
      } else {
        const variant = product.variants.find(v => v.weight === selectedWeight);
        if (variant) {
          setSelectedPrice(variant.price);
        }
      }
    }
  }, [selectedWeight, selectedColor, product]);

  const loadProduct = async () => {
    try {
      const data = await getProduct(id);
      setProduct(data);
      if (data.variants && data.variants.length > 0) {
        const isEspresso = data.category === 'Espresso Makineleri';
        setIsEspressoMachine(isEspresso);
        
        if (isEspresso && data.variants[0].color) {
          setSelectedColor(data.variants[0].color);
          setSelectedPrice(data.variants[0].price);
        } else if (data.variants[0].weight) {
          setSelectedWeight(data.variants[0].weight);
          setSelectedPrice(data.variants[0].price);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading product:', error);
      setLoading(false);
    }
  };

  const loadReviews = async () => {
    try {
      const data = await getReviews(id);
      setReviews(data);
    } catch (error) {
      console.error('Error loading reviews:', error);
    }
  };

  const handleAddToCart = async () => {
    if (!sessionId) {
      alert('Session bulunamadı. Lütfen sayfayı yenileyin.');
      return;
    }

    setAddingToCart(true);
    try {
      if (isEspressoMachine) {
        // Espresso machine - use color instead of weight
        await addToCart(sessionId, id, quantity, null, null, selectedColor);
      } else {
        // Coffee product - use weight and grind
        await addToCart(sessionId, id, quantity, selectedWeight, selectedGrind);
      }
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Sepete eklenirken bir hata oluştu.');
    } finally {
      setAddingToCart(false);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${
          i < Math.floor(rating)
            ? 'fill-amber-400 text-amber-400'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-44">
        <LoadingState text="Ürün yükleniyor..." />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-44">
        <ErrorState 
          message="Ürün bulunamadı" 
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-44 pb-16">
      <Helmet>
        <title>{product.name} | JUNTO® Kahve</title>
        <meta name="description" content={product.description} />
        <meta property="og:title" content={product.name} />
        <meta property="og:description" content={product.description} />
        <meta property="og:image" content={product.image} />
      </Helmet>
      
      <div className="container mx-auto px-4">
        {/* Back Button */}
        <Link
          to="/products"
          className="inline-flex items-center space-x-2 text-gray-600 hover:text-amber-600 mb-8 transition-colors"
          data-testid="back-to-products"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Ürünlere Dön</span>
        </Link>

        {/* Success Message */}
        {showSuccess && (
          <div className="fixed top-24 right-4 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg fade-in z-50" data-testid="success-message">
            ✓ Ürün sepete eklendi!
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="fade-in">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden sticky top-24">
              <img
                src={getProductImage()}
                alt={product.name}
                className="w-full h-auto object-cover"
                data-testid="product-detail-image"
              />
              {product.is_featured && (
                <div className="absolute top-4 right-4 bg-amber-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                  ⭐ Öne Çıkan
                </div>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div className="fade-in" style={{ animationDelay: '0.1s' }}>
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <span className="inline-block text-sm font-medium text-amber-600 bg-amber-50 px-3 py-1 rounded-full mb-4">
                {product.category}
              </span>

              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="product-detail-name">
                {product.name}
              </h1>

              <div className="flex items-center space-x-2 mb-6">
                <div className="flex items-center space-x-1">
                  {renderStars(product.rating)}
                </div>
                <span className="text-gray-600">
                  {product.rating} ({product.reviews_count} değerlendirme)
                </span>
              </div>

              <p className="text-gray-700 text-lg leading-relaxed mb-8">
                {product.description}
              </p>

              {/* Weight Selector (Coffee Products) */}
              {!isEspressoMachine && product.variants && product.variants.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Gramaj Seçin
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {product.variants.map((variant) => (
                      <button
                        key={variant.weight}
                        onClick={() => setSelectedWeight(variant.weight)}
                        className={`px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                          selectedWeight === variant.weight
                            ? 'border-junto-red bg-red-50 text-junto-red'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-junto-red-light'
                        }`}
                        data-testid={`weight-${variant.weight}`}
                      >
                        {variant.weight}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Color Selector (Espresso Machines) */}
              {isEspressoMachine && product.variants && product.variants.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Renk Seçin
                  </label>
                  <div className="flex gap-3">
                    {product.variants.map((variant) => (
                      <button
                        key={variant.color}
                        onClick={() => setSelectedColor(variant.color)}
                        className={`px-6 py-3 rounded-lg border-2 font-medium transition-all ${
                          selectedColor === variant.color
                            ? 'border-junto-red bg-red-50 text-junto-red'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-junto-red-light'
                        }`}
                        data-testid={`color-${variant.color}`}
                      >
                        {variant.color}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Grind Selector (Coffee Products Only) */}
              {!isEspressoMachine && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Öğütme Tercihi
                  </label>
                  <select
                    value={selectedGrind}
                    onChange={(e) => setSelectedGrind(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-junto-red focus:outline-none transition-all"
                    data-testid="grind-select"
                  >
                    {grindOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500 mt-2">
                    💡 Kahvenizi nasıl demlemeyi planlıyorsunuz?
                  </p>
                </div>
              )}

              <div className="flex items-baseline space-x-4 mb-8">
                <span className="text-4xl font-bold text-junto-red" data-testid="product-detail-price">
                  ₺{selectedPrice.toFixed(2)}
                </span>
                <span className="text-xl text-gray-500">{selectedWeight}</span>
              </div>

              {/* Stock Info */}
              <div className="mb-8">
                {product.stock > 20 ? (
                  <p className="text-green-600 font-medium" data-testid="stock-status">✓ Stokta var</p>
                ) : product.stock > 0 ? (
                  <p className="text-orange-600 font-medium">⚠ Son {product.stock} ürün!</p>
                ) : (
                  <p className="text-red-600 font-medium">✗ Stokta yok</p>
                )}
              </div>

              {/* Quantity Selector */}
              {product.stock > 0 && (
                <div className="mb-8">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adet
                  </label>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-lg font-bold transition-colors"
                      data-testid="decrease-quantity"
                    >
                      -
                    </button>
                    <span className="text-xl font-bold w-12 text-center" data-testid="quantity-display">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-lg font-bold transition-colors"
                      data-testid="increase-quantity"
                    >
                      +
                    </button>
                  </div>
                </div>
              )}

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                disabled={product.stock === 0 || addingToCart}
                className="w-full btn-primary bg-gradient-to-r from-junto-red to-junto-red-dark hover:from-junto-red-dark hover:to-junto-red-light text-white font-bold py-4 px-8 rounded-xl flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed transition-all mb-4"
                data-testid="add-to-cart-btn"
              >
                {addingToCart ? (
                  <span>Ekleniyor...</span>
                ) : (
                  <>
                    <ShoppingCart className="w-6 h-6" />
                    <span>Sepete Ekle</span>
                  </>
                )}
              </button>

              <Link
                to="/cart"
                className="w-full block text-center bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-4 px-8 rounded-xl transition-all"
                data-testid="go-to-cart-btn"
              >
                Sepete Git
              </Link>

              {/* Features */}
              <div className="grid grid-cols-2 gap-4 mt-8 pt-8 border-t border-gray-200">
                <div className="flex items-center space-x-3">
                  <Truck className="w-6 h-6 text-amber-600" />
                  <div>
                    <p className="font-medium text-sm">Hızlı Teslimat</p>
                    <p className="text-xs text-gray-600">2-3 iş günü</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Shield className="w-6 h-6 text-amber-600" />
                  <div>
                    <p className="font-medium text-sm">Güvenli Alışveriş</p>
                    <p className="text-xs text-gray-600">SSL şifreli</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Reviews */}
            {reviews.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-8 mt-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Müşteri Yorumları</h2>
                <div className="space-y-6">
                  {reviews.slice(0, 5).map((review) => (
                    <div key={review.id} className="border-b border-gray-200 pb-6 last:border-0">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{review.customer_name}</span>
                        <div className="flex items-center space-x-1">
                          {renderStars(review.rating)}
                        </div>
                      </div>
                      <p className="text-gray-600">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;